import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Factory, Package, ChevronRight, Check as CheckIcon } from "lucide-react";
import { fmtDate } from "@/utils/format";
import PageHeader from "@/components/PageHeader";

function TabBar({ current }) {
  return (
    <div className="flex gap-2 mb-4" data-testid="production-tabs">
      <Link
        to="/uretim"
        className={`px-4 py-2 border-2 border-[#09090B] font-mono text-xs uppercase tracking-widest ${current === "order" ? "bg-[#FF3B30] text-white" : "bg-white hover:bg-neutral-100"}`}
        data-testid="tab-order-based"
      >
        Sipariş Bazlı
      </Link>
      <Link
        to="/uretim/urun-bazli"
        className={`px-4 py-2 border-2 border-[#09090B] font-mono text-xs uppercase tracking-widest ${current === "product" ? "bg-[#FF3B30] text-white" : "bg-white hover:bg-neutral-100"}`}
        data-testid="tab-product-based"
      >
        Ürün Bazlı
      </Link>
    </div>
  );
}

export { TabBar };

export default function ProductionByProduct() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null); // grouped product row

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/production/by-product?_ts=${Date.now()}`);
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.toLocaleLowerCase("tr-TR");
    return rows.filter((r) => `${r.sku} ${r.product_name}`.toLocaleLowerCase("tr-TR").includes(s));
  }, [rows, q]);

  return (
    <div className="space-y-4" data-testid="production-by-product-page">
      <PageHeader
        kicker="Üretim"
        title="ÜRÜN BAZLI ÜRETİM"
        subtitle="Tüm aktif siparişlerdeki üretilecek ürünler tek satırda toplanmıştır. Bir ürüne tıklayıp birden fazla siparişi tek işlemle tamamlayın."
        border="border-[#FF3B30]"
        onRefresh={load}
        refreshing={loading}
        actions={
          <div className="flex items-center gap-2 px-4 py-1.5 border-2 border-[#FF3B30] text-[#FF3B30] font-mono uppercase text-xs">
            <Factory size={14} /> {rows.length} ürün
          </div>
        }
      />
      <TabBar current="product" />
      <Input
        placeholder="Ürün SKU veya adı ile ara..."
        value={q} onChange={(e) => setQ(e.target.value)}
        className="max-w-md rounded-none border-2 border-[#09090B] focus-visible:ring-0"
        data-testid="production-product-search"
      />
      <div className="bg-white border-2 border-[#09090B]">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-neutral-500 italic">
            {loading ? "Yükleniyor…" : "Bekleyen üretim yok"}
          </div>
        ) : (
          <ul className="divide-y-2 divide-neutral-200">
            {filtered.map((g) => (
              <li
                key={g.product_id}
                onClick={() => setSelected(g)}
                className="p-4 hover:bg-neutral-50 cursor-pointer flex items-center gap-4"
                data-testid={`production-product-row-${g.product_id}`}
              >
                <Package size={20} className="text-[#FF3B30] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-neutral-500">{g.sku || "—"}</div>
                  <div className="font-bold text-lg leading-tight">{g.product_name}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-mono uppercase text-neutral-500">Toplam Bekleyen</div>
                  <div className="text-3xl font-mono font-bold text-[#FF3B30]">{g.total_remaining} <span className="text-sm font-normal text-neutral-500">{g.unit}</span></div>
                </div>
                <div className="text-right ml-4">
                  <div className="text-[10px] font-mono uppercase text-neutral-500">Sipariş</div>
                  <div className="text-xl font-mono font-bold">{g.order_count}</div>
                </div>
                <ChevronRight size={20} className="text-neutral-400 shrink-0" />
              </li>
            ))}
          </ul>
        )}
      </div>
      {selected && <ProduceGroupDialog group={selected} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); }} />}
    </div>
  );
}


function ProduceGroupDialog({ group, onClose, onDone }) {
  const [picked, setPicked] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const toggle = (key) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const toggleAll = () => {
    if (picked.size === group.orders.length) setPicked(new Set());
    else setPicked(new Set(group.orders.map((o) => `${o.order_id}:${o.item_id}`)));
  };

  const selectedOrders = group.orders.filter((o) => picked.has(`${o.order_id}:${o.item_id}`));
  const totalSelected = selectedOrders.reduce((s, o) => s + Number(o.remaining || 0), 0);

  const submit = async () => {
    if (selectedOrders.length === 0) { toast.error("En az bir sipariş seçin"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/production/by-product/bulk-complete",
        selectedOrders.map((o) => ({ order_id: o.order_id, item_id: o.item_id })));
      toast.success(`${data.completed} kalem tamamlandı · ${data.total_qty} ${group.unit}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="rounded-none border-2 border-[#09090B] max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package size={18} className="text-[#FF3B30]" />
            {group.product_name}
            <span className="ml-auto text-xs font-mono text-neutral-500">Toplam Bekleyen: {group.total_remaining} {group.unit}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3 py-2 border-y-2 border-[#09090B] bg-neutral-50 px-3" data-testid="produce-selection-summary">
          <label className="flex items-center gap-2 cursor-pointer flex-1">
            <input
              type="checkbox"
              checked={picked.size === group.orders.length && group.orders.length > 0}
              onChange={toggleAll}
              className="w-4 h-4 accent-[#FF3B30]"
              data-testid="produce-select-all"
            />
            <span className="text-xs font-mono uppercase tracking-widest">Tümünü Seç</span>
          </label>
          <div className="text-xs font-mono">
            <span className="text-neutral-500">Seçili:</span>{" "}
            <strong data-testid="produce-selected-count">{selectedOrders.length}</strong> sipariş ·{" "}
            <strong className="text-[#FF3B30]" data-testid="produce-selected-qty">{totalSelected}</strong> {group.unit}
          </div>
        </div>

        <div className="divide-y">
          {group.orders.map((o) => {
            const key = `${o.order_id}:${o.item_id}`;
            const overdue = o.due_date && o.due_date < new Date().toISOString().slice(0, 10);
            return (
              <label
                key={key}
                className={`flex items-center gap-3 p-3 cursor-pointer ${picked.has(key) ? "bg-[#FF3B30]/5" : "hover:bg-neutral-50"}`}
                data-testid={`produce-order-row-${o.order_id}`}
              >
                <input
                  type="checkbox"
                  checked={picked.has(key)}
                  onChange={() => toggle(key)}
                  className="w-4 h-4 accent-[#FF3B30]"
                />
                <div className="flex-1">
                  <div className="font-mono font-bold">{o.order_number}</div>
                  <div className="text-sm text-neutral-600">{o.customer_name}</div>
                  {o.due_date && (
                    <div className={`text-[10px] font-mono uppercase mt-0.5 ${overdue ? "text-[#FF3B30] font-bold" : "text-neutral-500"}`}>
                      Termin: {fmtDate(o.due_date)}{overdue ? " · GECİKMİŞ" : ""}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-mono uppercase text-neutral-500">Kalan</div>
                  <div className="text-lg font-mono font-bold text-[#FF3B30]">{o.remaining} {group.unit}</div>
                  <div className="text-[10px] font-mono text-neutral-400">{o.produced_qty}/{o.quantity}</div>
                </div>
              </label>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-2 border-[#09090B]">Vazgeç</Button>
          <Button
            onClick={submit}
            disabled={saving || selectedOrders.length === 0}
            className="rounded-none bg-[#FF3B30] hover:bg-[#09090B] text-white"
            data-testid="produce-bulk-submit"
          >
            <CheckIcon size={16} className="mr-2" />
            {saving ? "İşleniyor…" : `Seçilenleri Üret (${selectedOrders.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
